import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mic, Image, MoreVertical, Phone, Video, Sparkles, Bot, User, Volume2, Copy, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react';

const createInitialMessages = (companyName) => [
  { 
    id: 1, 
    type: 'ai', 
    text: `Hi! 👋 I'm ${companyName} AI, your intelligent assistant. How can I help you today?`,
    time: '9:40 AM'
  },
  { 
    id: 2, 
    type: 'user', 
    text: "What's the weather like in Oslo?",
    time: '9:41 AM'
  },
  { 
    id: 3, 
    type: 'ai', 
    text: "Based on current data for Oslo, Norway:\n\n🌤️ **12°C** - Partly Cloudy\n💨 Wind: 8 km/h NW\n💧 Humidity: 65%\n\nPerfect weather for a walk in Vigelandsparken! Would you like me to suggest some outdoor activities?",
    time: '9:41 AM',
    hasActions: true
  },
];

export default function ChatPreview({ customization }) {
  const theme = customization?.theme || { primary: '#6366f1', secondary: '#8b5cf6', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' };
  const companyName = customization?.companyName || 'Nova';
  
  const [messages, setMessages] = useState(() => createInitialMessages(companyName));
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);

  const suggestions = [
    "📅 Set a reminder",
    "🎵 Play music",
    "📧 Check emails",
    "🗺️ Navigate home"
  ];

  const aiResponses = [
    `I'd be happy to help you with that! As your ${companyName} AI assistant, let me process your request... ✨\n\nHere's what I found:`,
    "Great question! Based on my analysis, here's what I recommend:\n\n",
    `I understand completely. Let me take care of that for you right away! 🚀`,
    "Interesting request! Here's a detailed response based on the latest information:",
  ];

  const handleSend = () => {
    if (!inputText.trim()) return;

    const userMessage = { 
      id: Date.now(), 
      type: 'user', 
      text: inputText,
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Simulate AI response with typing delay
    setTimeout(() => {
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: aiResponses[Math.floor(Math.random() * aiResponses.length)] + "\n\nIs there anything else I can help you with?",
        time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        hasActions: true
      };
      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 2000);
  };

  const handleSuggestion = (suggestion) => {
    setInputText(suggestion.slice(2).trim()); // Remove emoji
  };

  const toggleListening = () => {
    setIsListening(!isListening);
    if (!isListening) {
      setTimeout(() => setIsListening(false), 3000);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div style={{ 
      height: '100%', 
      background: `linear-gradient(180deg, #0f0f23 0%, ${theme.primary}10 50%, #16213e 100%)`,
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      color: 'white',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Status Bar */}
      <div style={{ 
        padding: '8px 16px', 
        display: 'flex', 
        justifyContent: 'space-between',
        fontSize: '11px',
        color: 'white'
      }}>
        <span style={{ fontWeight: '500' }}>9:41</span>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <span>📶</span>
          <span>🔋</span>
        </div>
      </div>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 16px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <motion.div
            animate={{ 
              boxShadow: [`0 0 0 0 ${theme.primary}60`, `0 0 0 12px ${theme.primary}00`, `0 0 0 0 ${theme.primary}00`]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: theme.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 20px ${theme.primary}60`
            }}
          >
            <Bot size={22} color="white" />
          </motion.div>
          <div>
            <h1 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>{companyName} AI</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <motion.span 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ 
                  display: 'inline-block',
                  width: '8px', height: '8px', 
                  background: '#22c55e', 
                  borderRadius: '50%',
                  boxShadow: '0 0 8px #22c55e'
                }} 
              />
              <span style={{ fontSize: '11px', color: `${theme.primary}cc` }}>Always ready to help</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Phone size={20} color={`${theme.primary}cc`} style={{ cursor: 'pointer' }} />
          <Video size={20} color={`${theme.primary}cc`} style={{ cursor: 'pointer' }} />
        </div>
      </div>

      {/* Messages */}
      <div style={{ 
        flex: 1, 
        padding: '14px 16px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px'
      }}>
        {messages.map((message, idx) => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
              gap: '4px'
            }}
          >
            {/* Avatar for AI */}
            {message.type === 'ai' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  background: theme.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Bot size={10} color="white" />
                </div>
                <span style={{ fontSize: '10px', color: '#64748b' }}>{companyName} AI</span>
              </div>
            )}
            
            <div style={{
              maxWidth: '85%',
              padding: '12px 14px',
              borderRadius: message.type === 'user' 
                ? '18px 18px 4px 18px'
                : '18px 18px 18px 4px',
              background: message.type === 'user'
                ? theme.gradient
                : 'rgba(255,255,255,0.08)',
              fontSize: '13px',
              lineHeight: 1.5,
              backdropFilter: message.type === 'ai' ? 'blur(10px)' : 'none',
              border: message.type === 'ai' ? '1px solid rgba(255,255,255,0.1)' : 'none',
              boxShadow: message.type === 'user' ? `0 4px 15px ${theme.primary}40` : 'none'
            }}>
              <div style={{ whiteSpace: 'pre-line' }}>{message.text}</div>
            </div>
            
            {/* Message actions for AI */}
            {message.type === 'ai' && message.hasActions && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{ display: 'flex', gap: '8px', marginTop: '4px' }}
              >
                {[
                  { icon: Copy, label: 'Copy' },
                  { icon: Volume2, label: 'Read' },
                  { icon: ThumbsUp, label: 'Good' },
                  { icon: RefreshCw, label: 'Retry' }
                ].map(action => (
                  <motion.button
                    key={action.label}
                    whileHover={{ scale: 1.1, background: `${theme.primary}30` }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      padding: '6px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#94a3b8',
                      fontSize: '9px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <action.icon size={10} />
                    {action.label}
                  </motion.button>
                ))}
              </motion.div>
            )}
            
            {/* Timestamp */}
            <span style={{ fontSize: '9px', color: '#4b5563', marginTop: '2px' }}>{message.time}</span>
          </motion.div>
        ))}

        {/* Typing Indicator */}
        <AnimatePresence>
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%',
                background: theme.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Bot size={10} color="white" />
              </div>
              <div style={{
                display: 'flex',
                gap: '4px',
                padding: '14px 16px',
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '18px 18px 18px 4px',
                backdropFilter: 'blur(10px)'
              }}>
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{ y: [0, -6, 0], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: theme.gradient
                    }}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Suggestions */}
      <div style={{ 
        padding: '0 16px 10px',
        display: 'flex',
        gap: '8px',
        overflowX: 'auto'
      }}>
        {suggestions.map((suggestion) => (
          <motion.button
            key={suggestion}
            onClick={() => handleSuggestion(suggestion)}
            whileHover={{ scale: 1.05, background: `${theme.primary}30` }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '8px 14px',
              background: 'rgba(255,255,255,0.08)',
              border: `1px solid ${theme.primary}30`,
              borderRadius: '20px',
              color: '#e2e8f0',
              fontSize: '11px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s'
            }}
          >
            {suggestion}
          </motion.button>
        ))}
      </div>

      {/* Input Area */}
      <div style={{
        padding: '12px 16px 20px',
        background: 'rgba(0,0,0,0.3)',
        borderTop: '1px solid rgba(255,255,255,0.08)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '28px',
          padding: '6px 8px 6px 16px',
          border: `1px solid ${theme.primary}30`
        }}>
          <motion.button 
            whileTap={{ scale: 0.9 }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <Image size={20} color={`${theme.primary}cc`} />
          </motion.button>
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder={`Message ${companyName}...`}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '14px',
              color: 'white'
            }}
          />
          <AnimatePresence mode="wait">
            {inputText.trim() ? (
              <motion.button
                key="send"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                onClick={handleSend}
                whileTap={{ scale: 0.9 }}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: theme.gradient,
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: `0 4px 15px ${theme.primary}60`
                }}
              >
                <Send size={16} color="white" />
              </motion.button>
            ) : (
              <motion.button
                key="mic"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                onClick={toggleListening}
                whileTap={{ scale: 0.9 }}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: isListening 
                    ? 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)' 
                    : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: isListening ? '0 0 20px rgba(239, 68, 68, 0.5)' : 'none'
                }}
              >
                <Mic size={16} color={isListening ? 'white' : `${theme.primary}cc`} />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
        
        {/* Listening indicator */}
        <AnimatePresence>
          {isListening && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '10px',
                color: '#ef4444'
              }}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                <Mic size={14} />
              </motion.div>
              <span style={{ fontSize: '11px' }}>Listening...</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
